// src/App.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Bill, Guest, ScanCategory, ReceiptItem, ScannedItem } from './types';
import { scanReceipt } from './utils/ocrEngine';
import { calculateBreakdown, formatCurrency } from './utils/billCalculator';
import { exportBillAsCSV, exportBillAsImage, exportBillAsPDF } from './utils/exportHelpers';
import { Camera, Plus, Share, ChevronDown, ChevronRight, Edit3, Trash2, Sparkles, ArrowLeft, ShieldCheck, Users, Receipt, LucideIcon } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

type CurrencyOption = {
  code: string;
  label: string;
  locale: string;
};

type AppViewMode = 'guided' | 'list';
type AppSection = 'guests' | 'items' | 'totals' | 'split' | 'notes';

const currencyOptions: CurrencyOption[] = [
  { code: 'USD', label: 'USD • US Dollar', locale: 'en-US' },
  { code: 'CAD', label: 'CAD • Canadian Dollar', locale: 'en-CA' },
  { code: 'EUR', label: 'EUR • Euro', locale: 'de-DE' },
  { code: 'GBP', label: 'GBP • British Pound', locale: 'en-GB' },
  { code: 'AUD', label: 'AUD • Australian Dollar', locale: 'en-AU' },
  { code: 'JPY', label: 'JPY • Japanese Yen', locale: 'ja-JP' }
];

type LandingFeature = {
  title: string;
  description: string;
  Icon: LucideIcon;
  accent: string;
};

const landingFeatures: LandingFeature[] = [
  {
    title: 'Smart receipt scanning',
    description: 'Snap a photo and SplitCheck instantly rebuilds every line item so the math stays accurate.',
    Icon: Camera,
    accent: 'text-brand-peach bg-brand-peach/10'
  },
  {
    title: 'Itemized splitting',
    description: 'Assign dishes to specific people so every person pays the fair amount.',
    Icon: Users,
    accent: 'text-brand-blue bg-brand-blue/10'
  },
  {
    title: 'Fair tax & tip',
    description: 'Enter fees once and SplitCheck calculates each guest’s proportional share.',
    Icon: Receipt,
    accent: 'text-brand-blue bg-brand-blue/10'
  },
  {
    title: 'Shareable summaries',
    description: 'Export polished text, image, and PDF summaries in one tap.',
    Icon: Share,
    accent: 'text-brand-peach bg-brand-peach/10'
  },
  {
    title: '100% private',
    description: 'Everything stays local in your browser session.',
    Icon: ShieldCheck,
    accent: 'text-brand-blue bg-brand-blue/10'
  }
];

const landingStats = [
  { value: 'Text Recognition', label: 'Receipts to editable items' },
  { value: 'Fair Math', label: 'Tax + tip by person' },
  { value: 'Share-ready', label: 'CSV, image, PDF exports' },
];

const APP_STORE_URL = 'https://apps.apple.com/il/app/splitcheck-scan-select-split/id6757835956';
const APP_LOGO_URL = `${process.env.PUBLIC_URL}/split-check-icon.png`;

const promotionalScreenshots = [
  {
    src: `${process.env.PUBLIC_URL}/screenshots/landing.png`,
    alt: 'SplitCheck landing screen',
    caption: 'Home and launch screen'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/receipt-details.png`,
    alt: 'Receipt details step',
    caption: 'Receipt details'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/whos-included.png`,
    alt: 'Guests step',
    caption: 'Who is included'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/add-items.png`,
    alt: 'Add items step',
    caption: 'Add and assign items'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/totals-fees.png`,
    alt: 'Totals and fees step',
    caption: 'Totals and fees'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/final-split.png`,
    alt: 'Final split step',
    caption: 'Final split breakdown'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/saved-summary.png`,
    alt: 'Saved summary card',
    caption: 'Saved summary card'
  },
  {
    src: `${process.env.PUBLIC_URL}/screenshots/summary-dark.jpg`,
    alt: 'Summary dark card',
    caption: 'Branded summary style'
  }
];

const getDefaultBillState = (): Bill => ({
  restaurantName: "",
  date: new Date(),
  items: [],
  guests: [],
  subtotal: 0,
  taxInput: 0,
  serviceChargeInput: 0,
  autoGratuityInput: 0,
  additionalGratuityInput: 0,
  taxIsPercentage: true,
  serviceIsPercentage: true,
  autoGratIsPercentage: true,
  addGratIsPercentage: true,
  notes: ""
});

const guidedSectionOrder: AppSection[] = ['guests', 'items', 'totals', 'notes', 'split'];

const App: React.FC = () => {
  // --- STATE ---
  const [isScanning, setIsScanning] = useState(false);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [newGuestName, setNewGuestName] = useState("");
  const [bill, setBill] = useState<Bill>(getDefaultBillState());
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState("");
  const [editingItemPrice, setEditingItemPrice] = useState("");
  
  const [correctedLines, setCorrectedLines] = useState<string[]>([]);
  const [rawOCRLines, setRawOCRLines] = useState<string[]>([]);
  const [showOCRLinesSection, setShowOCRLinesSection] = useState(false);
  const correctedLinesRef = useRef<HTMLDivElement | null>(null);
  const rawLinesRef = useRef<HTMLDivElement | null>(null);
  const isSyncingScroll = useRef(false);

  const syncScrollTo = (targetRef: React.MutableRefObject<HTMLDivElement | null>) => (
    event: React.UIEvent<HTMLDivElement>
  ) => {
    if (isSyncingScroll.current) {
      isSyncingScroll.current = false;
      return;
    }
    if (!targetRef.current) return;
    isSyncingScroll.current = true;
    targetRef.current.scrollTop = event.currentTarget.scrollTop;
  };

  const [expandedGuests, setExpandedGuests] = useState<string[]>([]);
  const [screen, setScreen] = useState<'marketing' | 'transition' | 'app'>('marketing');
  const [appViewMode, setAppViewMode] = useState<AppViewMode>('guided');
  const [guidedSectionIndex, setGuidedSectionIndex] = useState(0);
  const [albumIndex, setAlbumIndex] = useState(0);
  const isGuidedView = appViewMode === 'guided';
  const activeGuidedSection = guidedSectionOrder[guidedSectionIndex];
  const isSectionOpen = (section: AppSection) => !isGuidedView || activeGuidedSection === section;
  const goToPreviousGuidedSection = () => {
    setGuidedSectionIndex((prev) => Math.max(0, prev - 1));
  };
  const goToNextGuidedSection = () => {
    setGuidedSectionIndex((prev) => Math.min(guidedSectionOrder.length - 1, prev + 1));
  };
  const toggleGuestBreakdown = (guestId: string) => {
    setExpandedGuests(prev => prev.includes(guestId) ? prev.filter(id => id !== guestId) : [...prev, guestId]);
  };
  const goToNextAlbumShot = () => {
    setAlbumIndex((prev) => (prev + 1) % promotionalScreenshots.length);
  };
  const goToPreviousAlbumShot = () => {
    setAlbumIndex((prev) => (prev - 1 + promotionalScreenshots.length) % promotionalScreenshots.length);
  };
  const handleAppStoreLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (typeof window !== 'undefined') {
      window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
    }
  };
  const canExport = guests.length > 0;
  const selectedCurrency = currencyOptions[0];
  const formatWithCurrency = (value: number) => formatCurrency(value, selectedCurrency.code, selectedCurrency.locale);
  const handleSave = () => {
    if (!canExport) return;
    exportBillAsImage(bill, guests, selectedCurrency.code, selectedCurrency.locale);
  };

  // --- ACTIONS ---
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsScanning(true);
      try {
        setCorrectedLines([]);
        setRawOCRLines([]);
        const scanned = await scanReceipt(e.target.files[0]);
        setCorrectedLines(scanned.lines);
        setRawOCRLines(scanned.rawLines);
        processScannedItems(scanned.items);
      } catch (err) {
        alert("Failed to scan receipt");
      }
      setIsScanning(false);
    }
  };

  const processScannedItems = (items: ScannedItem[]) => {
    let newItems: ReceiptItem[] = [];
    let detectedTax = 0;
    let detectedTip = 0;
    let detectedServiceCharge = 0;
    
    items.forEach(item => {
      if (item.category === ScanCategory.Item) {
        newItems.push({ id: item.id, name: item.name, price: item.price, assignedGuestIds: [] });
      } else if (item.category === ScanCategory.Tax) {
        detectedTax += item.price;
      } else if (item.category === ScanCategory.Gratuity) {
        detectedTip += item.price;
      } else if (item.category === ScanCategory.Fee) {
        detectedServiceCharge += item.price;
      }
    });

    setBill(prev => ({
      ...prev,
      items: [...prev.items, ...newItems],
      subtotal: Math.round((prev.subtotal + newItems.reduce((sum, i) => sum + i.price, 0)) * 100) / 100,
      taxInput: detectedTax > 0
        ? Math.round((((prev.taxIsPercentage ? 0 : prev.taxInput) + detectedTax) * 100)) / 100
        : prev.taxInput,
      taxIsPercentage: detectedTax > 0 ? false : prev.taxIsPercentage,
      autoGratuityInput: detectedTip > 0
        ? Math.round((((prev.autoGratIsPercentage ? 0 : prev.autoGratuityInput) + detectedTip) * 100)) / 100
        : prev.autoGratuityInput,
      autoGratIsPercentage: detectedTip > 0 ? false : prev.autoGratIsPercentage,
      serviceChargeInput: detectedServiceCharge > 0
        ? Math.round((((prev.serviceIsPercentage ? 0 : prev.serviceChargeInput) + detectedServiceCharge) * 100)) / 100
        : prev.serviceChargeInput,
      serviceIsPercentage: detectedServiceCharge > 0 ? false : prev.serviceIsPercentage,
    }));
  };

  const addGuest = () => {
    if (!newGuestName) return;
    const newGuest = { id: uuidv4(), name: newGuestName };
    setGuests([...guests, newGuest]);
    setBill(prev => ({ ...prev, guests: [...prev.guests, newGuest] }));
    setNewGuestName("");
  };

  const toggleAssignment = (itemId: string, guestId: string) => {
    setBill(prev => {
      const updatedItems = prev.items.map(item => {
        if (item.id !== itemId) return item;
        const isAssigned = item.assignedGuestIds.includes(guestId);
        return {
          ...item,
          assignedGuestIds: isAssigned 
            ? item.assignedGuestIds.filter(id => id !== guestId)
            : [...item.assignedGuestIds, guestId]
        };
      });
      return { ...prev, items: updatedItems };
    });
  };

  const handleAddItem = () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName || newItemPrice.trim() === '') return;
    const parsedPrice = Number.parseFloat(newItemPrice);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) return;
    const newItem: ReceiptItem = { id: uuidv4(), name: trimmedName, price: parsedPrice, assignedGuestIds: [] };
    setBill(prev => ({
      ...prev,
      items: [...prev.items, newItem],
      subtotal: prev.subtotal + parsedPrice
    }));
    setNewItemName("");
    setNewItemPrice("");
  };

  const startEditingItem = (item: ReceiptItem) => {
    setEditingItemId(item.id);
    setEditingItemName(item.name);
    setEditingItemPrice(item.price.toString());
  };

  const cancelItemEdit = () => {
    setEditingItemId(null);
    setEditingItemName("");
    setEditingItemPrice("");
  };

  const saveItemEdits = (itemId: string) => {
    const trimmedName = editingItemName.trim();
    if (!trimmedName || editingItemPrice.trim() === '') return;
    const parsedPrice = Number.parseFloat(editingItemPrice);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) return;
    setBill(prev => {
      const target = prev.items.find(item => item.id === itemId);
      if (!target) return prev;
      const updatedItems = prev.items.map(item =>
        item.id === itemId ? { ...item, name: trimmedName, price: parsedPrice } : item
      );
      return { ...prev, items: updatedItems, subtotal: prev.subtotal + (parsedPrice - target.price) };
    });
    cancelItemEdit();
  };

  const deleteItem = (itemId: string) => {
    setBill(prev => {
      const target = prev.items.find(item => item.id === itemId);
      if (!target) return prev;
      const updatedItems = prev.items.filter(item => item.id !== itemId);
      return { ...prev, items: updatedItems, subtotal: prev.subtotal - target.price };
    });
    if (editingItemId === itemId) {
      cancelItemEdit();
    }
  };

  const calculateChargeValue = (value: number, isPercent: boolean) => 
    isPercent ? (bill.subtotal * (value / 100)) : value;

  const taxValue = calculateChargeValue(bill.taxInput, bill.taxIsPercentage);
  const serviceValue = calculateChargeValue(bill.serviceChargeInput, bill.serviceIsPercentage);
  const autoGratValue = calculateChargeValue(bill.autoGratuityInput, bill.autoGratIsPercentage);
  const addGratValue = calculateChargeValue(bill.additionalGratuityInput, bill.addGratIsPercentage);
  const totalBillAmount = bill.subtotal + taxValue + serviceValue + autoGratValue + addGratValue;
  const parsedNewItemPrice = Number.parseFloat(newItemPrice);
  const canAddManualItem =
    newItemName.trim().length > 0 &&
    newItemPrice.trim().length > 0 &&
    Number.isFinite(parsedNewItemPrice) &&
    parsedNewItemPrice >= 0;

  type ChargeControlProps = {
    label: string;
    value: number;
    isPercentage: boolean;
    subtotal: number;
    onValueChange: (value: number) => void;
    onToggleMode: (isPercent: boolean) => void;
  };

  const ChargeControlRow = ({ label, value, isPercentage, subtotal, onValueChange, onToggleMode }: ChargeControlProps) => {
    const [displayValue, setDisplayValue] = useState(() => value.toString());
    const [pendingSliderValue, setPendingSliderValue] = useState<number | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (!isFocused) {
        setDisplayValue(value.toString());
      }
    }, [value, isFocused]);

    const handleValueChange = (raw: string) => {
      const cleaned = raw.replace(/[^0-9.]/g, '');
      const decimalIndex = cleaned.indexOf('.');
      let normalized = cleaned;

      if (decimalIndex >= 0) {
        const integerPartRaw = cleaned.slice(0, decimalIndex);
        const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
        const fractionalRaw = cleaned.slice(decimalIndex + 1).replace(/\./g, '');
        const fractional = fractionalRaw.slice(0, 2);
        const shouldShowDot = fractional.length > 0 || cleaned.endsWith('.');
        normalized = `${integerPart}${shouldShowDot ? '.' : ''}${fractional}`;
      }

      setDisplayValue(normalized);

      if (normalized === '' || normalized === '.' || normalized === '0.') {
        onValueChange(0);
        return;
      }

      const parsed = parseFloat(normalized);
      if (Number.isFinite(parsed)) {
        onValueChange(Math.round(parsed * 100) / 100);
      }
    };

    const sliderStep = isPercentage ? 0.1 : 0.01;
    const sliderValue = Number.isFinite(value) ? value : 0;
    const sliderMax = isPercentage
      ? Math.max(100, sliderValue)
      : Math.max(100, subtotal, sliderValue) * 1.2;
    const sliderTrackValue = Math.min(sliderMax, pendingSliderValue ?? sliderValue);

    useEffect(() => {
      setPendingSliderValue(null);
    }, [value]);

    const handleSliderChange = (raw: string) => {
      const parsed = parseFloat(raw);
      if (!Number.isFinite(parsed)) return;
      const rounded = isPercentage
        ? Math.round(parsed * 10) / 10
        : Math.round(parsed * 100) / 100;
      setDisplayValue(rounded.toString());
      setPendingSliderValue(rounded);
    };

    const handleSliderCommit = () => {
      if (pendingSliderValue === null) return;
      onValueChange(pendingSliderValue);
      setPendingSliderValue(null);
    };

    const handleBlur = () => {
      setIsFocused(false);
      if (displayValue === '' || displayValue === '.' || displayValue === '0.') {
        setDisplayValue('0');
        onValueChange(0);
      }
    };

    return (
      <div className="flex flex-col gap-2 p-3 rounded-xl bg-gray-50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">{label}</span>
            <div className="flex items-center h-8 overflow-hidden border border-gray-200 rounded-full bg-white">
              <button
                type="button"
                onClick={() => onToggleMode(false)}
                className={`flex items-center justify-center px-3 text-[10px] font-semibold transition ${
                  isPercentage ? 'text-gray-400 hover:text-gray-600' : 'bg-brand-blue text-white'
                } h-full focus:outline-none`}
                aria-pressed={!isPercentage}
              >
                $
              </button>
              <button
                type="button"
                onClick={() => onToggleMode(true)}
                className={`flex items-center justify-center px-3 text-[10px] font-semibold transition ${
                  isPercentage ? 'bg-brand-blue text-white' : 'text-gray-400 hover:text-gray-600'
                } h-full focus:outline-none`}
                aria-pressed={isPercentage}
              >
                %
              </button>
            </div>
          </div>
          <input
            type="number"
            step={isPercentage ? '0.1' : '0.01'}
            min="0"
            inputMode="decimal"
            value={displayValue}
            onChange={e => handleValueChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            placeholder={isPercentage ? '0' : '0.00'}
            className="w-24 p-1 text-right bg-transparent border-b border-gray-300 outline-none focus:border-brand-blue font-mono"
          />
        </div>
        <input
          type="range"
          min={0}
          max={sliderMax}
          step={sliderStep}
          value={sliderTrackValue}
          onChange={(e) => handleSliderChange(e.target.value)}
          onPointerUp={handleSliderCommit}
          onMouseUp={handleSliderCommit}
          onTouchEnd={handleSliderCommit}
          aria-label={`${label} range`}
          className="charge-slider w-full h-[6px]"
        />
      </div>
    );
  };
  const setTaxValue = (value: number) => setBill(p => ({ ...p, taxInput: value }));
  const toggleTaxMode = (isPercent: boolean) => setBill(p => ({ ...p, taxIsPercentage: isPercent }));
  const setServiceValue = (value: number) => setBill(p => ({ ...p, serviceChargeInput: value }));
  const toggleServiceMode = (isPercent: boolean) => setBill(p => ({ ...p, serviceIsPercentage: isPercent }));
  const setAutoGratValue = (value: number) => setBill(p => ({ ...p, autoGratuityInput: value }));
  const toggleAutoGratMode = (isPercent: boolean) => setBill(p => ({ ...p, autoGratIsPercentage: isPercent }));
  const setAdditionalGratValue = (value: number) => setBill(p => ({ ...p, additionalGratuityInput: value }));
  const toggleAdditionalGratMode = (isPercent: boolean) => setBill(p => ({ ...p, addGratIsPercentage: isPercent }));

  // --- UI COMPONENTS ---
  const SectionHeader = ({
    guidedTitle,
    listTitle,
    section
  }: {
    guidedTitle: string;
    listTitle?: string;
    section: AppSection;
  }) => {
    if (!isGuidedView) {
      return (
        <div className="flex items-center justify-between w-full px-5 py-4 bg-white border-b border-[#e5e6ec]">
          <span className="text-xl leading-none font-semibold text-[#1d1f23]">{listTitle ?? guidedTitle}</span>
          <span className="text-[10px] font-semibold tracking-[0.3em] uppercase text-[#98a0ad]">List</span>
        </div>
      );
    }

    const sectionStep = guidedSectionOrder.indexOf(section) + 1;
    return (
      <div className="flex items-center justify-between w-full px-5 py-4 bg-white border-b border-[#e5e6ec]">
        <span className="text-xl leading-none font-semibold text-[#1d1f23]">{guidedTitle}</span>
        <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-[#98a0ad]">Step {sectionStep}/5</span>
      </div>
    );
  };

  const appInterface = (
    <div className="min-h-screen pb-28 text-gray-900 bg-[#ececf1]">
      
      <header className="sticky top-0 z-20 pt-safe-top bg-[#ececf1] border-b border-[#d8dbe3]">
        <div className="px-4 py-3 max-w-md mx-auto space-y-3">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setScreen('transition')}
              className="inline-flex items-center gap-1 text-base font-semibold text-[#2f547d]"
              aria-label="Return to landing page"
            >
              <ArrowLeft size={18} />
              <span>Back</span>
            </button>
            <div className="text-center leading-tight">
              <p className="text-sm font-semibold text-[#9196a3]">
                {isGuidedView ? `Guided Split • Step ${guidedSectionIndex + 1}/5` : 'List View'}
              </p>
              <p className="text-[30px] font-bold text-[#121418]">{bill.restaurantName || 'SplitCheck'}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative inline-flex items-center justify-center h-9 w-9 rounded-full bg-white border border-[#d7dae2] text-[#2f547d]">
                <input
                  type="file"
                  accept="image/*"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                  aria-label="Scan receipt image"
                />
                <Camera size={16} />
              </div>
              <button
                type="button"
                onClick={handleSave}
                className={`text-base font-bold ${canExport ? 'text-[#2f547d]' : 'text-[#a5acb8]'}`}
                disabled={!canExport}
              >
                Save
              </button>
            </div>
          </div>
          <div className="mx-auto flex max-w-[260px] rounded-full border border-[#d7dae2] bg-white p-1">
            <button
              type="button"
              onClick={() => setAppViewMode('guided')}
              aria-pressed={isGuidedView}
              className={`flex-1 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] transition ${
                isGuidedView ? 'bg-[#2f547d] text-white' : 'text-[#8a92a0] hover:text-[#2f547d]'
              }`}
            >
              Guided
            </button>
            <button
              type="button"
              onClick={() => setAppViewMode('list')}
              aria-pressed={!isGuidedView}
              className={`flex-1 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] transition ${
                !isGuidedView ? 'bg-[#2f547d] text-white' : 'text-[#8a92a0] hover:text-[#2f547d]'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-md px-3 mx-auto mt-5 space-y-4">
        
        {/* LOADING STATE */}
        {isScanning && (
          <div className="p-6 text-center bg-white border border-[#dfe2ea] rounded-[24px] animate-pulse">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-4 rounded-full border-brand-blue border-t-transparent animate-spin"></div>
              <p className="font-medium text-brand-blue">Scanning Receipt...</p>
            </div>
          </div>
        )}

        {/* 1. GUESTS CARD */}
        {isSectionOpen('guests') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <SectionHeader guidedTitle="1. Who is eating?" listTitle="Guests" section="guests" />
          
            <div className="p-5 space-y-4">
              <div className="flex gap-3">
                <input 
                  value={newGuestName}
                  onChange={(e) => setNewGuestName(e.target.value)}
                  placeholder="Guest Name"
                  className="flex-1 px-4 py-3 bg-[#f7f7fa] border border-[#e1e3ea] rounded-xl focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all placeholder:text-gray-400"
                  onKeyDown={(e) => e.key === 'Enter' && addGuest()}
                />
                <button 
                  onClick={addGuest} 
                  disabled={!newGuestName}
                  className="p-3 text-white transition-transform active:scale-95 bg-[#2f547d] rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={24} />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {guests.map(g => (
                  <div key={g.id} className="flex items-center gap-2 pl-3 pr-2 py-1.5 bg-[#dde3ec] text-[#2f547d] rounded-full">
                    <span className="text-sm font-semibold">{g.name}</span>
                  </div>
                ))}
                {guests.length === 0 && <span className="text-sm italic text-gray-400">Add guests to start splitting</span>}
              </div>
            </div>
          </div>
        )}

        {/* 2. ITEMS CARD */}
        {isSectionOpen('items') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <SectionHeader guidedTitle="2. Assign Items" listTitle="Items" section="items" />
          
            <div className="space-y-4">
              <div className="px-4 py-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-[0.4em]">Manual Items</p>
                <div className="flex flex-col gap-3 mt-3 sm:flex-row">
                  <input
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Item name"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition placeholder:text-gray-400 text-sm"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newItemPrice}
                    onChange={(e) => setNewItemPrice(e.target.value)}
                    placeholder="Price"
                    className="w-32 px-4 py-3 text-right bg-[#f7f7fa] border border-[#dfe2ea] rounded-2xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition placeholder:text-gray-400 text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleAddItem}
                    disabled={!canAddManualItem}
                    className={`p-3 rounded-2xl transition ${
                      canAddManualItem
                        ? 'bg-[#2f547d] text-white hover:bg-[#2b4c72]'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    aria-label="Add item"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {bill.items.map(item => {
                  const isEditingCurrent = editingItemId === item.id;
                  const parsedEditPrice = Number.parseFloat(editingItemPrice);
                  const canSaveEdit = isEditingCurrent && editingItemName.trim().length > 0 && editingItemPrice.trim().length > 0 && Number.isFinite(parsedEditPrice) && parsedEditPrice >= 0;
                  return (
                    <div key={item.id} className="p-4 transition-colors hover:bg-gray-50">
                      <div className="flex flex-col gap-1 mb-3">
                        <div className="flex items-start justify-between gap-3">
                          <span className="font-medium text-gray-900">{item.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-gray-600">{formatWithCurrency(item.price)}</span>
                            {!isEditingCurrent && (
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => startEditingItem(item)}
                                  className="p-2 rounded-full border border-gray-200 text-gray-600 hover:border-brand-blue/30"
                                  aria-label={`Edit ${item.name}`}
                                >
                                  <Edit3 size={14} />
                                  <span className="sr-only">Edit item</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteItem(item.id)}
                                  className="p-2 rounded-full border border-red-200 text-red-500 hover:border-red-300"
                                  aria-label={`Delete ${item.name}`}
                                >
                                  <Trash2 size={14} />
                                  <span className="sr-only">Delete item</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 pb-1 overflow-x-auto no-scrollbar">
                        {guests.map(guest => {
                          const isSelected = item.assignedGuestIds.includes(guest.id);
                          return (
                            <button
                              key={guest.id}
                              onClick={() => toggleAssignment(item.id, guest.id)}
                              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 border ${
                                isSelected 
                                  ? 'bg-brand-blue text-white border-brand-blue shadow-sm' 
                                  : 'bg-white text-gray-500 border-gray-200 hover:border-brand-blue/30'
                              }`}
                            >
                              {guest.name}
                            </button>
                          );
                        })}
                      </div>
                      {isEditingCurrent && (
                        <div className="mt-4 space-y-3 border-t border-dashed border-gray-200 pt-4">
                          <div className="flex flex-col gap-3 sm:flex-row">
                            <input
                              value={editingItemName}
                              onChange={(e) => setEditingItemName(e.target.value)}
                              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition placeholder:text-gray-400 text-sm"
                            />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingItemPrice}
                              onChange={(e) => setEditingItemPrice(e.target.value)}
                              className="w-32 px-4 py-3 text-right bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue outline-none transition placeholder:text-gray-400 text-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => saveItemEdits(item.id)}
                              disabled={!canSaveEdit}
                              className={`px-4 py-2 text-sm font-semibold rounded-2xl transition ${
                                canSaveEdit
                                  ? 'bg-brand-blue text-white hover:bg-brand-blue/90'
                                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              }`}
                            >
                              Save changes
                            </button>
                            <button
                              type="button"
                              onClick={cancelItemEdit}
                              className="px-4 py-2 text-sm font-semibold rounded-2xl border border-gray-200 text-gray-600 hover:border-brand-blue/30"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {bill.items.length === 0 && (
                  <div className="p-8 text-center text-gray-400">
                    Scan a receipt or manually add items to see them here
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {(correctedLines.length > 0 || rawOCRLines.length > 0) && (!isGuidedView || activeGuidedSection === 'items') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-100">
              <span>Text Recognition</span>
              <button
                type="button"
                onClick={() => setShowOCRLinesSection((prev) => !prev)}
                className="text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-blue transition hover:text-brand-blue/70 focus:outline-none"
              >
                {showOCRLinesSection ? 'Hide' : 'Show'}
              </button>
            </div>
            {showOCRLinesSection ? (
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                <div
                  ref={correctedLinesRef}
                  onScroll={syncScrollTo(rawLinesRef)}
                  className="p-4 text-[10px] font-mono text-gray-600 space-y-1 h-40 overflow-y-auto bg-gray-50"
                >
                  <div className="text-[9px] uppercase tracking-[0.3em] text-gray-400 mb-2">Corrected / Parsed</div>
                  {correctedLines.map((line, idx) => (
                    <div key={`corrected-${idx}`} className="whitespace-pre-line">
                      {line || <span className="text-gray-400">—</span>}
                    </div>
                  ))}
                </div>
                <div
                  ref={rawLinesRef}
                  onScroll={syncScrollTo(correctedLinesRef)}
                  className="p-4 text-[10px] font-mono text-gray-600 space-y-1 h-40 overflow-y-auto bg-gray-50"
                >
                  <div className="text-[9px] uppercase tracking-[0.3em] text-gray-400 mb-2">Raw / Pre-Clean</div>
                  {rawOCRLines.map((line, idx) => (
                    <div key={`raw-${idx}`} className="whitespace-pre-line">
                      {line || <span className="text-gray-400">—</span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}

        {/* 3. TOTALS CARD */}
        {isSectionOpen('totals') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <SectionHeader guidedTitle="3. Fees & Tax" listTitle="Fees & Tax" section="totals" />
          
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50">
                <label className="text-sm font-medium text-gray-600">Subtotal</label>
                <span className="font-mono text-gray-900">{formatWithCurrency(bill.subtotal)}</span>
              </div>
              <ChargeControlRow
                label="Tax"
                value={bill.taxInput}
                isPercentage={bill.taxIsPercentage}
                subtotal={bill.subtotal}
                onValueChange={setTaxValue}
                onToggleMode={toggleTaxMode}
              />
              <ChargeControlRow
                label="Service Charge"
                value={bill.serviceChargeInput}
                isPercentage={bill.serviceIsPercentage}
                subtotal={bill.subtotal}
                onValueChange={setServiceValue}
                onToggleMode={toggleServiceMode}
              />
              <ChargeControlRow
                label="Tip"
                value={bill.autoGratuityInput}
                isPercentage={bill.autoGratIsPercentage}
                subtotal={bill.subtotal}
                onValueChange={setAutoGratValue}
                onToggleMode={toggleAutoGratMode}
              />
              <ChargeControlRow
                label="Additional Gratuity"
                value={bill.additionalGratuityInput}
                isPercentage={bill.addGratIsPercentage}
                subtotal={bill.subtotal}
                onValueChange={setAdditionalGratValue}
                onToggleMode={toggleAdditionalGratMode}
              />
              <div className="flex items-center justify-between pt-3 mt-2 border-t border-dashed border-gray-200">
                <span className="text-sm font-semibold text-gray-700">Total</span>
                <span className="text-lg font-bold text-brand-blue">{formatWithCurrency(totalBillAmount)}</span>
              </div>
            </div>
          </div>
        )}

        {/* 4. NOTES CARD */}
        {isSectionOpen('notes') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <SectionHeader guidedTitle="4. Notes" listTitle="Notes" section="notes" />
            <div className="px-5 py-4 space-y-2">
              <textarea
                value={bill.notes}
                onChange={(event) => setBill(prev => ({ ...prev, notes: event.target.value }))}
                placeholder="Leave a note for your group (e.g., dietary requests, reminders, splits, etc.)."
                className="w-full min-h-[120px] px-4 py-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-2xl focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 outline-none transition resize-none"
              />
              <p className="text-xs text-gray-400">
                Notes are kept locally and included in exports when present.
              </p>
            </div>
          </div>
        )}

        {/* 5. FINAL SPLIT CARD */}
        {isSectionOpen('split') && (
          <div className="bg-white border border-[#dfe2ea] rounded-[24px] overflow-hidden">
            <SectionHeader guidedTitle="5. Final Split" listTitle="Final Split" section="split" />
          
            <div className="p-2">
              {guests.map(guest => {
                const breakdown = calculateBreakdown(guest, bill);
                const isGuestExpanded = expandedGuests.includes(guest.id);
                return (
                    <div key={guest.id} className="p-4 mb-2 bg-[#f8f8fb] rounded-xl border border-[#eceef3] last:mb-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-bold text-gray-900">{guest.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Subtotal: {formatWithCurrency(breakdown.subtotal)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-[#27b05f]">
                          {formatWithCurrency(breakdown.total)}
                        </p>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Total Owed</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleGuestBreakdown(guest.id)}
                      aria-expanded={isGuestExpanded}
                      className="flex items-center justify-between w-full px-0 mt-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-brand-blue"
                    >
                      <span>{isGuestExpanded ? 'Hide breakdown' : 'Show breakdown'}</span>
                      <ChevronDown size={16} className={`transition-transform ${isGuestExpanded ? 'rotate-180' : ''}`} />
                    </button>
                    {isGuestExpanded && (
                      <div className="mt-3 space-y-4 text-sm text-gray-600">
                        <div className="space-y-2">
                          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400">Items</p>
                          {breakdown.items.length > 0 ? (
                            <div className="space-y-1">
                              {breakdown.items.map((item, idx) => (
                                <div key={`${item.name}-${idx}`} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-600 leading-tight truncate">{item.name}</span>
                                  <span className="font-mono text-gray-900">{formatWithCurrency(item.amount)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400">No specific items assigned yet.</div>
                          )}
                        </div>
                        <div className="space-y-2 border-t border-dashed border-gray-200 pt-3">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Subtotal</span>
                            <span className="font-mono text-gray-700">{formatWithCurrency(breakdown.subtotal)}</span>
                          </div>
                          {[
                            { label: 'Tax', value: breakdown.taxShare },
                            { label: 'Service Charge', value: breakdown.serviceShare },
                            { label: 'Auto Gratuity', value: breakdown.autoGratuityShare },
                            { label: 'Additional Gratuity', value: breakdown.additionalGratuityShare },
                          ]
                            .filter(line => line.value !== 0)
                            .map(line => (
                              <div key={line.label} className="flex items-center justify-between text-xs text-gray-500">
                                <span>{line.label}</span>
                                <span className="font-mono text-gray-700">{formatWithCurrency(line.value)}</span>
                              </div>
                            ))}
                          <div className="flex items-center justify-between pt-1 text-sm font-semibold text-gray-900">
                            <span>Total</span>
                            <span className="font-mono">{formatWithCurrency(breakdown.total)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {guests.length === 0 && (
                <div className="p-6 text-center text-gray-400 text-sm">
                  Add guests and items to calculate the split
                </div>
              )}
              <div className="mt-4 border-t border-dashed border-gray-200 pt-4">
                <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-gray-400">
                  <Share size={14} />
                  Export
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <button
                    type="button"
                    disabled={!canExport}
                    onClick={() => exportBillAsCSV(bill, guests, selectedCurrency.code, selectedCurrency.locale)}
                    className={`flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-[0.3em] rounded-full border transition ${
                      canExport
                        ? 'bg-brand-blue text-white border-brand-blue shadow-sm'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    <span>CSV</span>
                  </button>
                  <button
                    type="button"
                    disabled={!canExport}
                    onClick={() => exportBillAsImage(bill, guests, selectedCurrency.code, selectedCurrency.locale)}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-[0.3em] rounded-full border transition ${
                      canExport
                        ? 'bg-brand-peach text-white border-brand-peach shadow-sm'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    Image
                  </button>
                  <button
                    type="button"
                    disabled={!canExport}
                    onClick={() => exportBillAsPDF(bill, guests, selectedCurrency.code, selectedCurrency.locale)}
                    className={`px-3 py-2 text-xs font-semibold uppercase tracking-[0.3em] rounded-full border transition ${
                      canExport
                        ? 'bg-brand-gray text-gray-900 border-gray-300 shadow-sm'
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    }`}
                  >
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FOOTER */}
        <div className="text-center pb-2 pt-2">
           <p className="text-xs text-gray-500">© 2026 SplitCheck • Private & Local</p>
        </div>

        {isGuidedView && (
          <div className="mx-1 px-4 py-3 rounded-full bg-[#f4f5f8] border border-[#e0e3ea] flex items-center justify-between text-[#2f547d]">
            <button
              type="button"
              onClick={goToPreviousGuidedSection}
              disabled={guidedSectionIndex === 0}
              className={`h-9 w-9 rounded-full grid place-items-center ${
                guidedSectionIndex === 0 ? 'bg-[#eef1f6] text-[#b7bfcc]' : 'bg-[#e7ebf1]'
              }`}
              aria-label="Go to previous section"
            >
              <ChevronRight size={18} className="rotate-180" />
            </button>
            <div className="text-center">
              <p className="text-2xl leading-none font-bold text-[#1d1f23]">
                {activeGuidedSection === 'guests'
                  ? 'Who is eating?'
                  : activeGuidedSection === 'items'
                    ? 'Assign Items'
                    : activeGuidedSection === 'totals'
                      ? 'Fees & Tax'
                      : activeGuidedSection === 'split'
                        ? 'Final Split'
                        : 'Notes'}
              </p>
              <div className="flex justify-center gap-1 mt-2">
                {guidedSectionOrder.map((_, index) => (
                  <span
                    key={`guided-dot-${index}`}
                    className={`h-2 rounded-full ${index === guidedSectionIndex ? 'bg-[#2f547d] w-6' : 'bg-[#cfd4de] w-2'}`}
                  />
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={goToNextGuidedSection}
              disabled={guidedSectionIndex === guidedSectionOrder.length - 1}
              className={`h-9 w-9 rounded-full grid place-items-center ${
                guidedSectionIndex === guidedSectionOrder.length - 1 ? 'bg-[#eef1f6] text-[#b7bfcc]' : 'bg-[#e7ebf1]'
              }`}
              aria-label="Go to next section"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}

      </main>
    </div>
  );

  const marketingPage = (
    <div className="min-h-screen bg-brand-blue text-white">
      <div className="max-w-6xl px-4 py-16 mx-auto space-y-12">
        <header className="grid gap-10 items-start lg:grid-cols-[1.05fr_minmax(360px,1fr)]">
          <div className="space-y-6">
            <div className="inline-flex items-center justify-center gap-3 rounded-full border border-brand-peach/25 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-brand-peach">
              <img
                src={APP_LOGO_URL}
                alt="SplitCheck logo"
                className="h-7 w-7 rounded-full border border-brand-peach/30"
              />
              SplitCheck
            </div>
            <h1 className="text-4xl font-bold leading-tight text-brand-peach md:text-5xl">
              SplitCheck
            </h1>
            <p className="max-w-3xl text-lg text-brand-peach/85">
              Aloha! Let&apos;s split the check.
            </p>
            <p className="max-w-3xl text-base text-white/78 md:text-lg">
              Scan a receipt, assign items, and calculate each person&apos;s fair share with the same blue-and-peach SplitCheck experience as the iPhone app.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setScreen('transition')}
                className="px-6 py-3 text-sm font-semibold text-brand-blue bg-brand-peach rounded-full hover:bg-[#f9d19b] transition"
              >
                Try it now free!
              </button>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={handleAppStoreLinkClick}
                className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-full border border-brand-peach/45 text-brand-peach hover:border-brand-peach hover:bg-white/5 transition"
              >
                Download on the App Store
              </a>
            </div>
            <div className="grid gap-4 mt-8 sm:grid-cols-3">
              {landingStats.map((stat) => (
                <div key={stat.label} className="px-5 py-4 rounded-2xl border border-brand-peach/20 bg-white/10 text-center">
                  <p className="text-lg font-semibold text-brand-peach">{stat.value}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.4em] text-white/65">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 -left-6 rounded-[36px] blur-3xl bg-brand-peach/30"></div>
            <div className="relative overflow-hidden rounded-[36px] border border-brand-peach/20 bg-white/10 p-6 shadow-2xl backdrop-blur">
              <div className="flex justify-center">
                <img
                  src={promotionalScreenshots[0].src}
                  alt={promotionalScreenshots[0].alt}
                  className="w-full max-w-[260px] rounded-3xl border border-brand-peach/30 shadow-[0_20px_50px_rgba(7,18,44,0.55)]"
                />
              </div>
            </div>
          </div>
        </header>

        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-brand-peach/80">Key features</p>
              <h2 className="text-3xl font-bold text-white">Every group meal handled</h2>
            </div>
            <div className="text-xs uppercase tracking-[0.4em] text-brand-peach/60">100% private • No accounts</div>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {landingFeatures.map((feature) => (
              <div key={feature.title} className="rounded-3xl border border-brand-peach/10 bg-white/8 p-6 shadow-xl backdrop-blur">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/10 ${feature.accent}`}>
                  <feature.Icon size={20} />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-white">{feature.title}</h3>
                <p className="mt-2 text-sm text-white/70">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-brand-peach/80">Photo Album</p>
              <h2 className="text-3xl font-bold text-white">App Screenshots</h2>
            </div>
            <span className="text-xs uppercase tracking-[0.3em] text-brand-peach/70">
              {albumIndex + 1} / {promotionalScreenshots.length}
            </span>
          </div>

          <div className="rounded-[28px] border border-brand-peach/20 bg-white/10 p-4 shadow-2xl backdrop-blur">
            <div className="relative mx-auto max-w-[290px]">
              <img
                src={promotionalScreenshots[albumIndex].src}
                alt={promotionalScreenshots[albumIndex].alt}
                className="h-[560px] w-full rounded-[26px] border border-brand-peach/30 object-cover shadow-[0_22px_55px_rgba(7,18,44,0.58)]"
              />

              <button
                type="button"
                onClick={goToPreviousAlbumShot}
                aria-label="Previous screenshot"
                className="absolute left-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/35 bg-black/45 text-white"
              >
                <ChevronRight size={18} className="rotate-180" />
              </button>
              <button
                type="button"
                onClick={goToNextAlbumShot}
                aria-label="Next screenshot"
                className="absolute right-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full border border-white/35 bg-black/45 text-white"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-brand-peach">{promotionalScreenshots[albumIndex].caption}</p>
              <div className="flex gap-1.5">
                {promotionalScreenshots.map((shot, index) => (
                  <button
                    key={shot.src}
                    type="button"
                    onClick={() => setAlbumIndex(index)}
                    aria-label={`Go to screenshot ${index + 1}`}
                    className={`h-2.5 rounded-full transition-all ${
                      index === albumIndex ? 'w-6 bg-brand-peach' : 'w-2.5 bg-white/40'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  const transitionPage = (
    <div className="min-h-screen bg-[#ececf1] text-[#171a1f]">
      <div className="max-w-md px-4 py-6 mx-auto">
        <div className="flex justify-end mb-4">
          <button
            type="button"
            className="h-12 w-12 rounded-full bg-[#f6f7fa] border border-[#d8dbe3] grid place-items-center text-[#2f547d]"
          >
            <Sparkles size={22} />
          </button>
        </div>

        <section className="rounded-[28px] border border-[#dde0e8] bg-[#f1f2f6] p-5">
          <div className="flex items-center gap-4">
            <img src={APP_LOGO_URL} alt="SplitCheck logo" className="h-16 w-16 rounded-2xl" />
            <div>
              <h1 className="text-5xl leading-none font-bold">SplitCheck</h1>
              <p className="mt-2 text-lg font-semibold text-[#2f547d]">Scan, Select, SplitCheck</p>
            </div>
          </div>
          <p className="mt-4 text-lg leading-tight text-[#6d7482]">
            Split the bill cleanly, save the details, and pick up where you left off.
          </p>
          <p className="mt-3 text-base leading-tight text-[#858b97]">
            Smart receipt splitting for dinner, drinks, and everything in between.
          </p>

          <div className="mt-5 rounded-2xl border border-[#e0e3ea] bg-[#fbfbfd] px-4 py-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-[#7f8593]">Most recent summary</p>
              <p className="text-2xl leading-tight font-bold">McDonald&apos;s</p>
              <p className="text-sm text-[#9aa1ae]">Mar 31, 2026 at 8:13 PM</p>
            </div>
            <ChevronRight size={20} className="text-[#9298a6]" />
          </div>
        </section>

        <div className="mt-5 space-y-4">
          <button
            type="button"
            onClick={() => setScreen('app')}
            className="w-full h-16 rounded-full bg-[#2f547d] text-white text-xl font-semibold flex items-center justify-center gap-2"
          >
            <Plus size={18} />
            Start New Split Check
          </button>
          <button
            type="button"
            className="w-full h-16 rounded-full bg-[#c7cdd7] text-[#2f547d] text-xl font-semibold"
          >
            Load Previous Check
          </button>
        </div>

        <section className="mt-5 rounded-[24px] border border-[#dde0e8] bg-[#f8f9fc] p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">Past Summaries</h2>
            <ChevronRight size={18} className="text-[#8f96a4]" />
          </div>
          <p className="mt-2 text-base font-semibold">9 saved checks ready to reload</p>
          <p className="mt-1 text-sm text-[#8f96a4]">Mar 31, 2026 at 8:13 PM</p>
        </section>

        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noreferrer"
          onClick={handleAppStoreLinkClick}
          className="mt-5 inline-flex w-full items-center justify-center rounded-full border border-[#2f547d]/20 bg-white py-3 text-sm font-semibold text-[#2f547d]"
        >
          Download on the App Store
        </a>
      </div>
    </div>
  );

  if (screen === 'app') return appInterface;
  if (screen === 'transition') return transitionPage;
  return marketingPage;
};

export default App;
